/**
 * Fit Creamery custom — "just bought" social proof popups (sections/fc-social-proof.liquid).
 *
 * Rules, in the order they apply:
 * - Nothing happens until the bundler is `triggerPercent` on screen. "On
 *   screen" is measured against the smaller of the bundler and the viewport,
 *   so a bundler taller than the phone can still reach 100%.
 * - Then: wait `startDelay`, show a card for `showFor`, wait `gap`, next card.
 * - With `pauseOffscreen`, a card that is due while the bundler is off screen
 *   waits for it to come back (a card already showing finishes its time).
 * - The cart opening hides the card; with `stopOnCart` it ends them for the
 *   visit. The close button ends them for good on that browser (localStorage).
 * - At most `maxPerVisit` per browser session, and the list carries on where
 *   it left off on the next page view (sessionStorage).
 * - On a mixed order the fanned bag photos shuffle while the card is up: every
 *   SHUFFLE_MS the front bag tucks behind the others (`shufflePhotos`).
 * - The dock rises above any `avoid` bar (the bundler's sticky Add to cart) that
 *   is on screen, and above iOS's bottom toolbar, the same way that bar does.
 *
 * Standalone; safe to keep across Eurus theme updates.
 */
(function () {
  'use strict';

  var OUT_MS = 340;
  var RESUME_MS = 1200;
  var FAN_OPEN_MS = 800; // the fan-open animation (260ms delay + 520ms)
  var SHUFFLE_MS = 1600;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Put each bag of a card in its fan slot after `turns` shuffles. Slot 0 is the
     front; the bag that just left the front gets .is-tucking for the tuck move. */
  function setSlots(bags, turns, tucking) {
    var n = bags.length;
    for (var i = 0; i < n; i++) {
      var slot = (((i - turns) % n) + n) % n;
      var cls = 'fc-sp__bag fc-sp__bag--s' + slot;
      if (tucking && slot === n - 1) cls += ' is-tucking';
      bags[i].className = cls;
    }
  }

  function store(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  }

  // Survives across visits: a shopper who closes a popup never sees another.
  function remember(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  }

  function shuffled(n) {
    var a = [];
    for (var i = 0; i < n; i++) a.push(i);
    for (var j = n - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var t = a[j];
      a[j] = a[k];
      a[k] = t;
    }
    return a;
  }

  function SocialProof(root) {
    this.root = root;
    this.dock = root.querySelector('[data-fc-sp-dock]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-fc-sp-card]'));
    try {
      this.s = JSON.parse(root.getAttribute('data-settings') || '{}');
    } catch (e) {
      this.s = {};
    }
    this.key = 'fcSp:' + (root.getAttribute('data-section-id') || '') + ':';
    this.current = null;
    this.timer = null;
    this.inView = false;
    this.started = false;
    this.waiting = false;
    this.cartOpen = false;
    this.stopped = false;

    if (!this.dock || !this.cards.length) return;
    this.root.style.setProperty('--fc-sp-show', (this.s.showFor || 6) + 's');

    if (window.Shopify && Shopify.designMode) {
      this.initDesignMode();
      return;
    }
    if (store(this.key + 'off') === '1' || remember('fcSp:dismissed') === '1') return;

    this.order = this.loadOrder();
    this.bindClose();
    this.watchTrigger();
    this.watchCart();
    this.watchLift();
  }

  SocialProof.prototype.loadOrder = function () {
    var n = this.cards.length;
    var saved = null;
    try {
      saved = JSON.parse(store(this.key + 'order') || 'null');
    } catch (e) {}
    if (!saved || saved.length !== n) {
      if (this.s.shuffle) {
        saved = shuffled(n);
      } else {
        saved = [];
        for (var i = 0; i < n; i++) saved.push(i);
      }
      store(this.key + 'order', JSON.stringify(saved));
    }
    return saved;
  };

  SocialProof.prototype.count = function (add) {
    var c = parseInt(store(this.key + 'count') || '0', 10) || 0;
    if (add) store(this.key + 'count', String((c += add)));
    return c;
  };

  SocialProof.prototype.stop = function () {
    this.stopped = true;
    clearTimeout(this.timer);
    store(this.key + 'off', '1');
  };

  SocialProof.prototype.schedule = function (seconds) {
    var self = this;
    clearTimeout(this.timer);
    this.timer = setTimeout(function () { self.next(); }, seconds * 1000);
  };

  SocialProof.prototype.next = function () {
    if (this.stopped || this.current) return;
    if (this.count() >= (this.s.maxPerVisit || 4)) return this.stop();
    if (this.cartOpen || (this.s.pauseOffscreen && !this.inView)) {
      this.waiting = true;
      return;
    }
    var idx = parseInt(store(this.key + 'idx') || '0', 10) || 0;
    var card = this.cards[this.order[idx % this.order.length]];
    store(this.key + 'idx', String((idx + 1) % this.order.length));
    this.count(1);
    this.show(card);
  };

  SocialProof.prototype.show = function (card) {
    var self = this;
    this.current = card;
    card.classList.remove('is-out');
    card.hidden = false;
    void card.offsetWidth; // restart the CSS animations
    card.classList.add('is-in');
    this.startShuffle(card);
    clearTimeout(this.timer);
    this.timer = setTimeout(function () { self.hide(); }, (this.s.showFor || 6) * 1000);
  };

  SocialProof.prototype.startShuffle = function (card) {
    var self = this;
    var photo = card.querySelector('.fc-sp__photo');
    var bags = photo ? photo.querySelectorAll('.fc-sp__bag') : [];
    this.stopShuffle();
    if (bags.length < 2) return;
    setSlots(bags, 0, false);
    photo.classList.add('is-opening');
    this.shuffleTimer = setTimeout(function () {
      photo.classList.remove('is-opening');
      if (self.s.shufflePhotos === false || reducedMotion) return;
      var turns = 0;
      self.shuffleTimer = setInterval(function () {
        setSlots(bags, ++turns, true);
      }, SHUFFLE_MS);
    }, FAN_OPEN_MS);
  };

  SocialProof.prototype.stopShuffle = function () {
    clearTimeout(this.shuffleTimer);
    clearInterval(this.shuffleTimer);
  };

  SocialProof.prototype.hide = function (andThen) {
    var self = this;
    var card = this.current;
    clearTimeout(this.timer);
    if (!card) return;
    this.stopShuffle();
    card.classList.remove('is-in');
    card.classList.add('is-out');
    setTimeout(function () {
      card.classList.remove('is-out');
      card.hidden = true;
      // Back to the starting order while hidden, so nothing slides on the next show.
      var bags = card.querySelectorAll('.fc-sp__bag');
      if (bags.length > 1) setSlots(bags, 0, false);
      if (self.current === card) self.current = null;
      if (andThen === 'stop' || self.stopped) return;
      if (self.count() >= (self.s.maxPerVisit || 4)) return self.stop();
      self.schedule(self.s.gap || 12);
    }, OUT_MS);
  };

  /* The close button. On touch it acts on touchend and cancels that touch's
     default actions. That matters: hiding the card on the tap lets the browser's
     follow-up click land on whatever was underneath (a bundler button, a link),
     so the shopper's "close" also pressed the page. preventDefault() on touchend
     stops that click being generated at all, and acting on the real finger
     release also gets round the theme swallowing a page's first tap. A touch
     that moved (a scroll that started on the button) does nothing. Mouse and
     keyboard use the ordinary click. */
  SocialProof.prototype.bindClose = function () {
    var self = this;
    this.cards.forEach(function (card) {
      var btn = card.querySelector('[data-fc-sp-close]');
      if (!btn) return;
      var dismiss = function () {
        remember('fcSp:dismissed', '1');
        self.stop();
        self.hide('stop');
      };
      var startX = 0;
      var startY = 0;
      btn.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0];
        startX = t.clientX;
        startY = t.clientY;
      }, { passive: true });
      btn.addEventListener('touchend', function (e) {
        var t = e.changedTouches[0];
        if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) return;
        e.preventDefault();
        dismiss();
      }, { passive: false });
      btn.addEventListener('click', dismiss);
    });
  };

  SocialProof.prototype.watchTrigger = function () {
    var self = this;
    var target = document.querySelector(this.s.trigger || '.product-bundler');
    if (!target || !('IntersectionObserver' in window)) return;
    var need = Math.min(100, Math.max(1, this.s.triggerPercent || 50)) / 100;
    var thresholds = [];
    for (var i = 0; i <= 20; i++) thresholds.push(i / 20);

    new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var full = Math.min(e.boundingClientRect.height, vh) || 1;
      var shown = e.isIntersecting ? e.intersectionRect.height / full : 0;
      self.inView = shown >= need - 0.001;

      if (self.inView && !self.started) {
        self.started = true;
        self.schedule(self.s.startDelay || 0);
      } else if (self.inView && self.waiting) {
        self.waiting = false;
        self.schedule(RESUME_MS / 1000);
      }
    }, { threshold: thresholds }).observe(target);
  };

  SocialProof.prototype.watchCart = function () {
    var self = this;
    var onToggle = function (open) {
      if (open === self.cartOpen) return;
      self.cartOpen = open;
      if (open) {
        if (self.s.stopOnCart) self.stop();
        if (self.current) self.hide();
      } else if (self.waiting && self.started) {
        self.waiting = false;
        self.schedule(RESUME_MS / 1000);
      }
    };
    if (window.FC && typeof FC.onCartToggle === 'function') {
      FC.onCartToggle(onToggle);
    } else {
      // Fallback: fc-hide-floating-on-cart.js toggles this class on <html>.
      var html = document.documentElement;
      new MutationObserver(function () {
        onToggle(html.classList.contains('fc-cart-open'));
      }).observe(html, { attributes: true, attributeFilter: ['class'] });
    }
  };

  /* Keep the dock above the bundler's sticky bar (and anything else listed in
     `avoid`) whenever one is on screen, and above iOS's bottom toolbar. Bars
     slide with a transform, so this measures where each actually is rather
     than trusting its classes. */
  SocialProof.prototype.watchLift = function () {
    var self = this;
    var bars = [];
    if (this.s.avoid) {
      try {
        bars = Array.prototype.slice.call(document.querySelectorAll(this.s.avoid));
      } catch (e) {}
    }
    var vv = window.visualViewport;
    var raf = null;
    var last = -1;

    var measure = function () {
      raf = null;
      var vh = window.innerHeight;
      var lift = vv ? Math.max(0, vh - vv.height - vv.offsetTop) : 0;
      bars.forEach(function (bar) {
        var r = bar.getBoundingClientRect();
        if (r.height && r.top < vh - 1 && r.bottom > 0) lift = Math.max(lift, vh - r.top);
      });
      lift = Math.round(lift);
      if (lift !== last) {
        last = lift;
        self.dock.style.setProperty('--fc-sp-lift', lift + 'px');
      }
    };
    var queue = function () {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    // A bar's slide lasts ~300ms: re-measure through it, not just at the end.
    var settle = function () {
      queue();
      setTimeout(queue, 120);
      setTimeout(queue, 340);
    };

    bars.forEach(function (bar) {
      new MutationObserver(settle).observe(bar, { attributes: true, attributeFilter: ['class', 'style'] });
      bar.addEventListener('transitionend', queue);
    });
    if (vv) {
      vv.addEventListener('resize', queue);
      vv.addEventListener('scroll', queue);
    }
    window.addEventListener('resize', queue);
    window.addEventListener('scroll', queue, { passive: true });
    measure();
  };

  /* Theme editor: show the first card, then whichever Purchase block is picked. */
  SocialProof.prototype.initDesignMode = function () {
    var self = this;
    this.root.classList.add('is-design-mode');
    var pick = function (card) {
      self.cards.forEach(function (c) {
        c.classList.remove('is-in');
        c.hidden = true;
      });
      card.hidden = false;
      void card.offsetWidth;
      card.classList.add('is-in');
      self.startShuffle(card);
    };
    pick(this.cards[0]);
    document.addEventListener('shopify:block:select', function (e) {
      var card = self.root.querySelector('[data-block-id="' + e.detail.blockId + '"]');
      if (card) pick(card);
    });
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-fc-social-proof]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__fcSp) continue;
      roots[i].__fcSp = new SocialProof(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
